<script lang="ts">
  import { onDestroy, onMount, untrack } from 'svelte';
  import type { DateValue } from '@internationalized/date';
  import { Button } from '$lib/components/ui/button';
  import * as Card from '$lib/components/ui/card';
  import Skeleton from '$lib/components/Skeleton.svelte';
  import { cn } from '$lib/utils';
  import {
    buildRoomRedirectUrl,
    fetchRoomPrice,
    formatPrice,
  } from '$lib/api';
  import {
    addNights,
    nightsBetween,
    toISODate,
    tomorrow,
  } from '$lib/dates';
  import {
    isFutureOrToday,
    loadSelection,
    onSelectionChange,
    parseISODate,
  } from '$lib/storage';
  import type {
    RoomConfig,
    RoomPricingState,
    WidgetConfig,
  } from '$lib/types';
  import { t } from '$lib/i18n';

  type Props = {
    config: WidgetConfig;
    room: RoomConfig;
  };

  let { config, room }: Props = $props();

  const i18n = $derived(t(config.lang));

  const hasRoomId = $derived(
    Boolean(room.idRoom) ||
      (Boolean(room.roomtcode) && Boolean(room.catcode)),
  );

  function getInitialSelection(): {
    checkin: DateValue;
    checkout: DateValue;
    guests: number;
  } {
    const fallback = {
      checkin: tomorrow() as DateValue,
      checkout: addNights(tomorrow(), 1) as DateValue,
      guests: 2,
    };
    const saved = loadSelection();
    if (!saved) return fallback;
    const cIn = parseISODate(saved.checkin);
    const cOut = parseISODate(saved.checkout);
    if (!cIn || !cOut) return fallback;
    if (!isFutureOrToday(cIn)) return fallback;
    if (nightsBetween(cIn, cOut) < 1) return fallback;
    return { checkin: cIn, checkout: cOut, guests: saved.guests };
  }

  const initial = getInitialSelection();
  let checkin = $state<DateValue>(initial.checkin);
  let checkout = $state<DateValue>(initial.checkout);
  let guests = $state<number>(initial.guests);

  let pricing = $state<RoomPricingState>({ status: 'idle' });
  let currentController: AbortController | null = null;
  let debounceTimer: ReturnType<typeof setTimeout> | null = null;
  let unsubscribeSelection: (() => void) | null = null;

  const query = $derived({
    startdate: toISODate(checkin),
    enddate: toISODate(checkout),
    residents: guests,
    idRoom: room.idRoom,
    roomtcode: room.roomtcode,
    catcode: room.catcode,
  });

  async function loadPrice() {
    if (!config.token) {
      pricing = { status: 'error', message: i18n.missingToken };
      return;
    }
    if (!hasRoomId) {
      pricing = { status: 'idle' };
      return;
    }
    currentController?.abort();
    const ctrl = new AbortController();
    currentController = ctrl;
    pricing = { status: 'loading' };
    try {
      const data = await fetchRoomPrice(
        config,
        untrack(() => query),
        ctrl.signal,
      );
      if (ctrl.signal.aborted) return;
      pricing = { status: 'success', data };
    } catch (err) {
      if (ctrl.signal.aborted) return;
      const message = err instanceof Error ? err.message : i18n.unknownError;
      pricing = { status: 'error', message };
    }
  }

  function scheduleLoad() {
    if (debounceTimer) clearTimeout(debounceTimer);
    debounceTimer = setTimeout(loadPrice, 300);
  }

  function applySelection(saved: ReturnType<typeof loadSelection>) {
    if (!saved) return;
    const cIn = parseISODate(saved.checkin);
    const cOut = parseISODate(saved.checkout);
    if (!cIn || !cOut) return;
    if (!isFutureOrToday(cIn)) return;
    if (nightsBetween(cIn, cOut) < 1) return;
    checkin = cIn;
    checkout = cOut;
    guests = saved.guests;
    scheduleLoad();
  }

  onMount(() => {
    loadPrice();
    unsubscribeSelection = onSelectionChange((sel) => {
      applySelection(sel);
    });
  });

  onDestroy(() => {
    currentController?.abort();
    if (debounceTimer) clearTimeout(debounceTimer);
    unsubscribeSelection?.();
  });

  let hasMounted = false;

  $effect(() => {
    query.startdate;
    query.enddate;
    query.residents;
    query.idRoom;
    query.roomtcode;
    query.catcode;
    if (!hasMounted) {
      hasMounted = true;
      return;
    }
    untrack(() => scheduleLoad());
  });

  const bestPrice = $derived(
    pricing.status === 'success' ? pricing.data.bestPrice : null,
  );
  const hasPrice = $derived(bestPrice !== null && bestPrice > 0);

  const bookHref = $derived(
    hasRoomId ? buildRoomRedirectUrl(config, query) : undefined,
  );
</script>

<Card.Root
  size="sm"
  class={cn(
    'qs-room-root quick-search-root w-full max-w-md',
    'flex-row items-center gap-3 px-4 py-3',
    'border border-border shadow-sm ring-0',
  )}
>
  <div class="qs-room-info flex min-w-0 flex-1 flex-col gap-0.5">
    {#if room.label}
      <span class="qs-room-label truncate text-xs font-medium text-muted-foreground">
        {room.label}
      </span>
    {/if}

    {#if !hasRoomId}
      <span class="qs-room-missing-config text-sm italic text-muted-foreground">
        {i18n.missingRoomConfig}
      </span>
    {:else if pricing.status === 'idle' || pricing.status === 'loading'}
      <div class="qs-room-loading flex items-baseline gap-1.5">
        <Skeleton class="h-3 w-12" />
        <Skeleton class="h-5 w-16" />
      </div>
    {:else if pricing.status === 'error'}
      <span class="qs-room-error text-xs text-destructive" aria-live="polite" title={pricing.message}>
        {pricing.message}
      </span>
    {:else if pricing.status === 'success'}
      {#if hasPrice}
        <div class="qs-room-price-row flex items-baseline gap-1.5">
          <span class="qs-room-price-from text-[10px] uppercase tracking-wide text-muted-foreground">
            {i18n.priceFrom}
          </span>
          <span class="qs-room-price text-lg font-bold leading-tight text-foreground">
            {formatPrice(bestPrice as number, config.lang)}
          </span>
        </div>
      {:else}
        <span class="qs-room-unavailable text-sm italic text-muted-foreground">
          {i18n.unavailable}
        </span>
      {/if}
    {/if}
  </div>

  <Button
    size="sm"
    href={bookHref}
    target="_blank"
    rel="noopener noreferrer"
    class="qs-room-book-btn shrink-0 rounded-md text-xs font-semibold"
    disabled={!hasRoomId || (pricing.status === 'success' && !hasPrice)}
  >
    {i18n.book}
  </Button>
</Card.Root>
